import React, { useState, useEffect } from "react";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { gql, useQuery } from "@apollo/client";
import Pagination from "../components/Pagination";
import { usePagination } from "../components/usePagination";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

const PAYMENTS_QUERY = gql`
  query Payments($first: Int, $after: String, $last: Int, $before: String) {
    payments(first: $first, after: $after, last: $last, before: $before) {
      edges {
        node {
          id
          payer {
            firstName
            lastName
            email
          }
          asset {
            regNumber
            plateNumber
          }
          amount
          status
          createdAt
          beneficiary {
            firstName
            lastName
            email
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

const formatStatus = (status: string) => {
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const Row = (props: { row: any; key?: any }) => {
  const { row } = props;

  // Check if payer and beneficiary are the same
  const isSelf = row.payer.email === row.beneficiary.email;

  // Format payment ID to 6 digits
  const formatPaymentId = (id: string) => {
    return id.slice(-6).padStart(6, "0");
  };

  return (
    <React.Fragment>
      <tr>
        <td>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Avatar
              size="sm"
              alt={`${row.payer.firstName} ${row.payer.lastName}`}
            />
            <div>
              <Typography level="body-xs">
                {row.payer.firstName} {row.payer.lastName}
              </Typography>
              <Typography
                level="body-xs"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "200px",
                }}
              >
                {row.payer.email}
              </Typography>
            </div>
          </Box>
        </td>
        <td>
          {isSelf ? (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <PeopleAltIcon sx={{ width: "2rem", height: "1.5rem" }} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Avatar
                size="sm"
                alt={`${row.beneficiary.firstName} ${row.beneficiary.lastName}`}
              />
              <div>
                <Typography level="body-xs">
                  {row.beneficiary.firstName} {row.beneficiary.lastName}
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "200px",
                  }}
                >
                  {row.beneficiary.email}
                </Typography>
              </div>
            </Box>
          )}
        </td>
        <td>
          <Typography level="body-xs">{row.asset.regNumber}</Typography>
        </td>
        <td>
          <Typography level="body-xs">{row.asset.plateNumber}</Typography>
        </td>
        <td>
          <Typography level="body-xs">{row.amount}</Typography>
        </td>
        <td>
          <Typography level="body-xs">{formatPaymentId(row.id)}</Typography>
        </td>
        <td>
          <Typography level="body-xs">{formatStatus(row.status)}</Typography>
        </td>
        <td>
          <Typography level="body-xs">
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }).format(new Date(row.createdAt))}
          </Typography>
        </td>
      </tr>
    </React.Fragment>
  );
};

const PaymentsTable = () => {
  const [pageSize] = useState(10);
  const { data, loading, error, fetchMore, refetch } = useQuery(
    PAYMENTS_QUERY,
    {
      variables: {
        first: pageSize,
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );

  const {
    pageInfo,
    isLoadingMore,
    handlePageChange,
    canGoNext,
    canGoPrevious,
  } = usePagination({
    pageSize,
    data,
    fetchMore,
    getPageInfo: (data) => data.payments.pageInfo,
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) return prev;
      return {
        payments: {
          ...fetchMoreResult.payments,
          edges: [...fetchMoreResult.payments.edges],
        },
      };
    },
  });

  // Setup WebSocket connection
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (wsData.type === "payment_updated") {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Typography level="body-lg" color="danger">
          Error loading data: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: "15%", padding: "12px 6px" }}>Payer</th>
              <th style={{ width: "15%", padding: "12px 6px" }}>Beneficiary</th>
              <th style={{ width: "10%", padding: "12px 6px" }}>Asset</th>
              <th style={{ width: "10%", padding: "12px 6px" }}>
                Plate Number
              </th>
              <th style={{ width: "10%", padding: "12px 6px" }}>Amount</th>
              <th style={{ width: "8%", padding: "12px 6px" }}>Receipt ID</th>
              <th style={{ width: "10%", padding: "12px 6px" }}>Status</th>
              <th style={{ width: "12%", padding: "12px 6px" }}>Created At</th>
            </tr>
          </thead>
          <tbody>
            {loading || isLoadingMore ? (
              <tr>
                <td colSpan={8}>
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size="sm" />
                  </Box>
                </td>
              </tr>
            ) : (
              data?.payments?.edges?.map(({ node: row }) => (
                <Row key={row.id} row={row} />
              ))
            )}
          </tbody>
        </Table>
      </Sheet>
      <Pagination
        pageInfo={pageInfo}
        isLoadingMore={isLoadingMore}
        onPageChange={handlePageChange}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
      />
    </React.Fragment>
  );
};

const Payments = () => {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          Payments
        </Typography>
      </Box>
      <PaymentsTable />
    </>
  );
};

export default Payments;
